import { useState } from "react";
import { History, Loader2, Smartphone, Unplug, Wrench } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ago } from "@/components/sms/smsUtils";
import { PhoneConnectDialog } from "@/components/sms/PhoneConnectDialog";
import { PhoneHistoryDialog } from "@/components/sms/PhoneHistoryDialog";

const QUIET_MS = 20 * 60 * 1000;
const TONE = {
  on: ["bg-emerald-500 animate-pulse", "text-emerald-800 bg-emerald-50 border-emerald-200"],
  quiet: ["bg-red-500", "text-red-800 bg-red-50 border-red-200"],
  off: ["bg-[#9A9A90]", "text-[#6B6B61] bg-[#F4F3EC] border-[#E0DFD6]"],
};
const btn = "h-9 px-3.5 inline-flex items-center gap-1.5 rounded-full text-xs font-semibold border transition-[background-color,transform] active:scale-95 disabled:opacity-50";
const GREEN = `${btn} bg-[#385C4B] text-white border-[#385C4B] hover:bg-[#2C4A3C]`;
const LIGHT = `${btn} bg-white text-[#385C4B] border-[#385C4B]/40 hover:bg-[#EFEEE6]`;

export const PhoneCard = ({ status, onChanged }) => {
  const [dlg, setDlg] = useState("");
  const [base, setBase] = useState(0);
  const [busy, setBusy] = useState(false);
  const p = status?.phone_app || {};
  const now = status?.server_ts || Date.now();
  const signal = Math.max(p.last_ping_ts || 0, p.device_last_seen_ts || 0);
  const seen = Math.max(signal, p.last_sms_ts || 0);
  const state = !status ? "load" : !p.linked ? "off" : now - seen < QUIET_MS ? "on" : "quiet";
  const [dot, pill] = TONE[state === "load" ? "off" : state];
  const label = { load: "Loading…", on: `Connected${p.device_name ? ` · ${p.device_name}` : ""}`, quiet: `Phone ${ago(seen, now).replace(" ago", "")} se chup hai`, off: "Not connected" }[state];
  const openConnect = () => { setBase(p.last_sms_ts || 0); setDlg("connect"); };
  const disconnect = async () => {
    if (!window.confirm("Phone app ko disconnect karna hai? Uske baad phone se SMS aana band ho jayega.")) return;
    setBusy(true);
    try {
      await api.post("/sms/phone/disconnect");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white border border-[#E0DFD6] rounded-xl p-4 sm:p-5 space-y-3" data-testid="sms-phone-card">
      <div className="flex items-center gap-2 text-sm font-semibold text-[#3A3A35]">
        <Smartphone className="w-4 h-4 text-[#385C4B]" /> Phone Direct
        <span className="text-[10px] font-mono uppercase bg-[#385C4B]/10 text-[#385C4B] px-2 py-0.5 rounded-full">Main</span>
      </div>
      <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-semibold ${pill}`} data-testid="sms-phone-state">
        <span className={`w-2 h-2 rounded-full ${dot}`} />{label}
      </span>
      <div className="text-xs text-[#6B6B61] flex flex-wrap gap-x-4 gap-y-1">
        <span data-testid="sms-phone-last-sms">Last SMS: {ago(p.last_sms_ts, now)}</span>
        {p.connected_ts && <span data-testid="sms-phone-signal">Phone signal: {ago(signal, now)}</span>}
        {p.history?.requested_ts && <span data-testid="sms-phone-history-info">Purane SMS mangaye: {ago(p.history.requested_ts, now)}</span>}
      </div>
      {state === "off" && <p className="text-xs text-[#6B6B61]">Phone se seedha SMS aayenge — koi Gmail login nahi, isliye logout bhi nahi.</p>}
      {state === "quiet" && (
        <p className="text-xs font-semibold text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="sms-phone-quiet">
          Phone ka net, battery saver ya SMS Gateway app check karo. Net aate hi ruke hue SMS apne aap aa jayenge.
        </p>
      )}
      {p.error && <p className="text-xs font-semibold text-red-700" data-testid="sms-phone-error">{p.error}</p>}
      <div className="flex flex-wrap gap-2">
        {state === "off" && <button onClick={openConnect} className={GREEN} data-testid="sms-phone-connect-btn"><Smartphone className="w-3.5 h-3.5" /> Phone Connect</button>}
        {p.connected_ts && <button onClick={() => setDlg("history")} className={GREEN} data-testid="sms-phone-history-btn"><History className="w-3.5 h-3.5" /> Purane SMS mangao</button>}
        {state !== "off" && state !== "load" && <button onClick={openConnect} className={LIGHT} data-testid="sms-phone-guide-btn"><Wrench className="w-3.5 h-3.5" /> Setup guide</button>}
        {p.connected_ts && (
          <button onClick={disconnect} disabled={busy} className={`${btn} bg-white text-[#C8553D] border-[#C8553D]/40 hover:bg-red-50`} data-testid="sms-phone-disconnect-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unplug className="w-3.5 h-3.5" />} Disconnect
          </button>
        )}
      </div>
      <PhoneConnectDialog open={dlg === "connect"} phone={p} base={base} onClose={() => setDlg("")} onDone={onChanged} />
      <PhoneHistoryDialog open={dlg === "history"} onClose={() => setDlg("")} onDone={onChanged} />
    </section>
  );
};
